/**
 * Consent-aware lazy dispatch for Meta *events*.
 *
 * The ticket is captured synchronously at the real event occurrence, before
 * the dynamic import. After the module resolves the event runs only if ads
 * consent is current AND no withdrawal was recorded after the ticket — so an
 * event from before a withdrawal can never send after a later re-grant.
 * Configuration-only imports (context, consent sync) do not use this.
 * Tracking failures are swallowed: callers' cart/payment flows never depend
 * on this promise rejecting.
 */
import { hasAdsConsent } from "@/lib/cookie-consent";
import { adsWithdrawnSince, currentConsentGeneration } from "@/lib/consentTransitions";

type MetaModule = typeof import("@/lib/metaPixel");

export interface MetaTicket {
  gen: number;
}

export function captureMetaTicket(): MetaTicket | null {
  if (typeof window === "undefined" || !hasAdsConsent()) return null;
  return { gen: currentConsentGeneration() };
}

export function metaTicketValid(t: MetaTicket | null): boolean {
  return !!t && hasAdsConsent() && !adsWithdrawnSince(t.gen);
}

let loader: () => Promise<MetaModule> = () => import("@/lib/metaPixel");
/** Test seam: inject a deferred module loader. */
export function __setMetaLoaderForTests(fn: (() => Promise<MetaModule>) | null): void {
  loader = fn ?? (() => import("@/lib/metaPixel"));
}

export function metaLazy(fn: (m: MetaModule) => void): Promise<void> {
  const ticket = captureMetaTicket();
  if (!ticket) return Promise.resolve();
  return loader()
    .then((m) => {
      if (metaTicketValid(ticket)) fn(m);
    })
    .catch(() => {});
}
